import React, { useState } from "react";
import PropTypes from "prop-types";
import TextHighlight from "./TextHighlight";
import { useEffect } from "react";
import Lnk from "./Lnk";
import useDebounce from "../../hook/useDebounce";
import { useRef } from "react";
import useEffectWithoutLoad from "../../hook/useEffectWithoutLoad";
import TaskEditor from "../smart/TaskEditor";

const collator = new Intl.Collator("en", {
  numeric: true,
  sensitivity: "base",
});

const rowsPerPageOptions = [10, 20, 50, 100, 500, 1000];

const Table = (props) => {
  const [debounce] = useDebounce(200);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [sortDirection, setSortDirection] =
    useState(1); /* Can equal 1 (normal) or -1 (reversed) */
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedColumnIndex, setSelectedColumn] = useState(
    props.cols.indexOf(props.cols.find((col) => col["sortCol"] != undefined))
  ); //Finds first col that is sortable and makes it default selected
  const selectedColumn = props.cols[selectedColumnIndex];
  const [checkValues, setCheckValues] = useState([]);
  const [rows, setRows] = useState(props.rows);

  const [filtered, setFiltered] = useState([]);
  const [sorted, setSorted] = useState([]);
  const [page, setPage] = useState([]);
  const [pageCount, setPageCount] = useState([]);

  const sort = (rows, selectedColumn, sortDirection) => {
    if (selectedColumn == undefined) return rows;

    return [...rows].sort((aKey, bKey) => {
      const a = props.rows[aKey];
      const b = props.rows[bKey];
      let aString = a[selectedColumn.sortCol]?.toString();
      let bString = b[selectedColumn.sortCol]?.toString();
      if (!aString) return sortDirection * -1;
      if (!bString) return sortDirection;
      return sortDirection * collator.compare(aString, bString);
    });
  };

  useEffect(() => {
    setRows(props.rows);
  }, [props.rows]); // Keeps state in sync if props change

  /* 
    const handleSpellcheck = async (languageCode) => {
            // Loop through all the rows in the current page and collect data only from the specified column
            const dataToCheck = Object.values(props.rows)
            .map((row) => row["target"]?.toString() || "") // Replace "target" with the correct column key
            .filter((text) => text); // Remove empty/null values
           
            if (dataToCheck.length > 0) {
              // Send data to OpenAI for spellchecking
              try {
                const response = await fetch(`${window.API_URL}/task/spellcheck.php`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ text: dataToCheck, language: languageCode })
                });
                
                const result = await response.json();
    
            console.log(result);
            const correctedWords = result.corrected || [];
            const originalWords = result.original || [];
            
            if (correctedWords.length === originalWords.length) {
              
                    
                 
            }
    
            else {
              console.error("Original and corrected arrays do not match in length");
            }
              } catch (error) {
                console.error("Error during spellchecking:", error);
              }
            }
          };*/

  useEffect(() => {
    setRows(props.rows);
  }, [props.rows]); // Keeps state in sync if props change

  const filter = (rows, searchKeyword) => {
    if (!searchKeyword) return rows;
    const lwrCaseSubstrings = searchKeyword
      .toLowerCase()
      .split(" ")
      .map((word) => word);

    return rows.filter((rowKey) => {
      const row = props.rows[rowKey];
      const words = [];
      Object.values(row).forEach((colVal) => {
        if (!colVal) colVal = "";
        return colVal
          .toString()
          .toLowerCase()
          .split(" ")
          .forEach((word) => words.push(word));
      });
      return lwrCaseSubstrings.every((i) =>
        words.some((word) => word.includes(i))
      );
    });
  };

  const slice = (rows, currentPage, rowsPerPage) => {
    let minRowIndex = (currentPage - 1) * rowsPerPage;
    let maxRowIndex = minRowIndex + rowsPerPage;
    return rows.slice(minRowIndex, maxRowIndex);
  };

  const onCheck = (e) => {
    const checkValue = e.target.getAttribute("checkValue");
    if (!checkValue) return;

    if (e.target.checked) {
      setCheckValues([...checkValues, checkValue]);
    } else {
      let array = [...checkValues];
      array.splice(array.indexOf(checkValue), 1);
      setCheckValues(array);
    }
  };

  useEffect(() => {
    if (props.newCheckValues) props.newCheckValues(checkValues);
  }, [checkValues]);

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  //Rows changed
  useEffect(() => {
    if (
      (props.shouldRecalculateOnRowChange &&
        props.shouldRecalculateOnRowChange()) ||
      !props.shouldRecalculateOnRowChange
    ) {
      const _filtered = filter(Object.keys(props.rows), searchKeyword);
      const _sorted = sort(_filtered, selectedColumn, sortDirection);
      const _pageCount = Math.ceil(_sorted.length / rowsPerPage);
      const _currentPage = clamp(currentPage, 1, _pageCount);
      const _page = slice(_sorted, _currentPage, rowsPerPage);

      setFiltered(_filtered);
      setSorted(_sorted);
      setPage(_page);
      setPageCount(_pageCount);
      setCurrentPage(_currentPage);

      setCheckValues([]);
    } else {
    }
  }, [props.rows]);

  //Search changed
  useEffect(() => {
    const _filtered = filter(Object.keys(props.rows), searchKeyword);
    const _sorted = sort(_filtered, selectedColumn, sortDirection);
    const _pageCount = Math.ceil(_sorted.length / rowsPerPage);
    const _currentPage = 1;
    const _page = slice(_sorted, _currentPage, rowsPerPage);

    setFiltered(_filtered);
    setSorted(_sorted);
    setPage(_page);
    setPageCount(_pageCount);
    setCurrentPage(_currentPage);
  }, [searchKeyword]);

  //Sort changed. Don't need to recalculate filter
  useEffect(() => {
    const _sorted = sort(filtered, selectedColumn, sortDirection);
    const _pageCount = Math.ceil(_sorted.length / rowsPerPage);
    const _page = slice(_sorted, currentPage, rowsPerPage);

    setSorted(_sorted);
    setPage(_page);
    setPageCount(_pageCount);
  }, [sortDirection, selectedColumnIndex]);

  //Sorted changed
  useEffect(() => {
    const _pageCount = Math.ceil(sorted.length / rowsPerPage);
    const _currentPage = clamp(currentPage, 1, _pageCount);
    const _page = slice(sorted, _currentPage, rowsPerPage);

    setPage(_page);
    setPageCount(_pageCount);
    setCurrentPage(_currentPage);
  }, [rowsPerPage, currentPage]);

  const headerClicked = (key) => {
    if (key === selectedColumnIndex) setSortDirection(sortDirection * -1);
    setSelectedColumn(key);
  };

  const onNavInput = (e) => {
    let value = parseInt(e.target.value);

    if (value < 1) value = 1;
    if (value > pageCount) value = pageCount;
    if (isNaN(value)) value = null;

    setCurrentPage(value);
  };

  const prevPage = (e) => {
    if (currentPage - 1 < 1) return;
    setCurrentPage(currentPage - 1);
  };

  const nextPage = (e) => {
    if (currentPage + 1 > pageCount) return;
    setCurrentPage(currentPage + 1);
  };

  //should refactor to renderTextData
  const renderData = (data, options) => {
    if (options.hidden) return;

    // If the data contains words that need to be highlighted (based on search or other conditions)
    if (options.highlight !== false && typeof data === "string") {
      const highlight = (
        <TextHighlight
          text={data}
          highlight={[...searchKeyword.split(" ")]}
          className="highlight__table-search"
        />
      );

      if (options.here) {
        return (
          <Data key={options.key}>
            <Lnk here={options.here}>{highlight}</Lnk>
          </Data>
        );
      }

      return <Data key={options.key}>{highlight}</Data>;
    }

    // For regular non-highlighted data
    return <Data key={options.key}>{data}</Data>;
  };

  const renderTextareaData = (value, options = {}) => {
    if (options.hidden) return;

    return (
      <Data className={"table__data--textarea"} key={options.key}>
        <TextArea value={value} onInput={options.onInput} />
      </Data>
    );
  };

  let rowsToRender;

  if (props.customRow != true) {
    rowsToRender = page.map((rowKey) => {
      const row = props.rows[rowKey];
      if (!row) return;
      return (
        <Row {...(props.getRowAttributes ? props.getRowAttributes(row) : [])}>
          {props.checkbox == true ? (
            <Data>
              <input
                checkValue={props.getCheckValue(row)}
                checked={checkValues.includes(props.getCheckValue(row))}
                onChange={onCheck}
                className="checkbox"
                type="checkbox"
              />
            </Data>
          ) : (
            ""
          )}
          {props.renderRow(row, renderData, { renderTextareaData })}
        </Row>
      );
    });
  } else {
    rowsToRender = page.map((rowKey) => {
      const row = props.rows[rowKey];
      if (!row) return;
      return props.renderRow(row, renderData, { renderTextareaData });
    });
  }

  return (
    <div className={"table-container "}>
      {!props.title && !props.titleContent ? (
        ""
      ) : (
        <div className="table__title-container">
          <span className="table__title">{props.title}</span>
          <span className="table__title-content">{props.titleContent}</span>
        </div>
      )}
      <table className={"table " + props.className}>
        <thead>
          <tr>
            {props.checkbox == true ? <th className="table__header"></th> : ""}
            {props.cols.map((col, index) => {
              if (col.hidden) return;
              let selectedClass =
                index === selectedColumnIndex ? "table__header--selected" : "";

              let sortIcon =
                index !== selectedColumnIndex ? (
                  ""
                ) : sortDirection === 1 ? (
                  <i className="fas fa-sort-amount-up-alt icon table__sort-icon" />
                ) : (
                  <i className="fas fa-sort-amount-up icon table__sort-icon" />
                );
              return (
                <th
                  key={index}
                  className={`table__header ${selectedClass} ${col.class}`}
                  onClick={() => {
                    if (col.sortCol !== undefined) {
                      headerClicked(index);
                    }
                  }}
                >
                  {col.name}
                  {/* {sortIcon} */}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          <tr className="table__row">
            <td
              className="table__data table__data--search"
              colSpan={props.cols.length + 1}
            >
              <input
                className="table__search input"
                type="text"
                placeholder="Search"
                onChange={(e) => {
                  const value = e.target.value;
                  debounce(() => {
                    setSearchKeyword(value);
                  });
                }}
              />
              <select
                className="table__row-select select select--secondary"
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <option
                    key={option}
                    value={option}
                    selected={option == rowsPerPage}
                  >
                    {option}
                  </option>
                ))}
              </select>
            </td>
          </tr>

          {page.length > 0 ? (
            ""
          ) : (
            <tr className="table__row">
              <td className="table__data">
                <div className="spinner" style={{ margin: "0px" }} />
              </td>
            </tr>
          )}
          {rowsToRender}
        </tbody>
      </table>

      {/* {navButtonsJSX} */}

      {pageCount <= 1 ? (
        ""
      ) : (
        <div className="table__nav-container">
          <button
            className={
              "button table__nav-button " +
              (currentPage == 1 ? "table__nav-button--selected" : "")
            }
            onClick={(e) => setCurrentPage(1)}
          >
            1
          </button>

          <button className="button table__nav-button" onClick={prevPage}>
            <i class="fas fa-chevron-left" aria-hidden="true"></i>
          </button>

          <input
            className="input table__nav-input"
            onChange={onNavInput}
            onSelect={(e) => e.target.select}
            contenteditable="true"
            value={currentPage}
          />

          <button className="button table__nav-button" onClick={nextPage}>
            <i class="fas fa-chevron-right" aria-hidden="true"></i>
          </button>

          <button
            className={
              "button table__nav-button " +
              (currentPage == pageCount ? "table__nav-button--selected" : "")
            }
            onClick={(e) => setCurrentPage(pageCount)}
          >
            {pageCount}
          </button>
        </div>
      )}
    </div>
  );
};

Table.propTypes = {
  cols: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
  titleContent: PropTypes.string,
};

const Row = (props) => (
  <tr className={"table__row " + props.className}>{props.children}</tr>
);
const Data = (props) => (
  <td className={"table__data " + props.className}>{props.children}</td>
);

const TextArea = React.memo(
  (props) => {
    const ref = useRef();
    const [value, setValue] = useState();

    useEffect(() => setValue(props.value), [props.value]);

    useEffectWithoutLoad(() => {
      if (props.focus) ref.current.focus();
    }, [props.focus]);

    return (
      <React.Fragment>
        <textarea
          spellCheck={props.spellcheck === false ? false : true}
          onFocus={props.onFocus}
          ref={ref}
          onInput={(e) => {
            if (props.onInput) props.onInput(e.target.value);
            setValue(e.target.value);
          }}
          hasFocus={props.focus}
          value={value}
          className={"input table__textarea " + props.className}
          tabindex="-1"
        ></textarea>
        <div className="table__textarea-expander">{value}.</div>
      </React.Fragment>
    );
  },
  (prev, next) => {
    return prev.value === next.value && prev.focus === next.focus;
  }
);

export { Table, Row, TextArea };
